/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { format, startOfDay } from 'date-fns';
import { find } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { formatShipByDate } from 'common/utils';
import DateField from 'common/forms/DateField';
import useRoles from 'common/hooks/useRoles';
import PatientBanner from 'common/modules/PatientBanner';
import PatientNameBanner from 'common/modules/PatientNameBanner';
import {
  applyUrgentTag,
  clearShipByDateOverride,
  removeUrgentTag,
  setLineItemShipByDate,
} from 'store/thunks/workQueueThunks';

const hasTag = (tags, tagName) => !!(tags && find(tags, { tagName }));

const ShipByDateOverrideDialog = ({ open, onClose, lineItem, showPatientBanner }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { overrideShipByDate } = useRoles();

  const [isCleared, setIsCleared] = useState(false);
  const [urgentChecked, setUrgentChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const isOverridden = hasTag(lineItem?.tags, 'SHIP_BY_OVERRIDDEN');
  const isUrgent = hasTag(lineItem?.tags, 'URGENT');

  const getPrefillDate = () => {
    if (lineItem?.shipByDate) {
      const [y, m, d] = lineItem.shipByDate.substring(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return undefined;
  };

  const formInitialValues = useMemo(
    () => ({
      shipByDate: getPrefillDate(),
      reason: isOverridden ? lineItem?.overrideReason || '' : '',
    }),
    // formKey is the only intended dependency — recompute only when the dialog
    // re-opens, not on every render (prevents MUI DatePicker from resetting a
    // prefilled past date when unrelated state like urgentChecked changes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formKey]
  );

  useEffect(() => {
    if (open) {
      setIsCleared(false);
      setUrgentChecked(isUrgent);
      setFormKey((k) => k + 1);
    }
  }, [open, isUrgent]);

  const handleClose = (_, reason) => {
    if (reason !== 'backdropClick') onClose();
  };

  const handleSave = async ({ shipByDate, reason } = {}) => {
    const needsDate = urgentChecked && !isCleared && !(isUrgent && !urgentChecked);
    if (needsDate && !shipByDate) {
      enqueueSnackbar('Please select a valid ship-by date', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      if (isCleared) {
        await dispatch(
          clearShipByDateOverride({
            lineItemId: lineItem.lineItemId,
            mpi: lineItem.mpi,
            orderId: lineItem.orderId,
          })
        );
        enqueueSnackbar(
          'Override ship by date removed — ship by date has been restored to the system-calculated ship by date',
          { variant: 'success' }
        );
      } else if (urgentChecked && shipByDate) {
        await dispatch(
          applyUrgentTag({
            lineItemId: lineItem.lineItemId,
            shipByDate: format(shipByDate, 'yyyy-MM-dd'),
            reason: reason || undefined,
            mpi: lineItem.mpi,
            orderId: lineItem.orderId,
          })
        );
        enqueueSnackbar('Urgent tag applied', { variant: 'success' });
      } else if (!urgentChecked && isUrgent) {
        await dispatch(
          removeUrgentTag({
            lineItemId: lineItem.lineItemId,
            shipByDate: shipByDate ? format(shipByDate, 'yyyy-MM-dd') : undefined,
            reason: reason || undefined,
            mpi: lineItem.mpi,
            orderId: lineItem.orderId,
          })
        );
        enqueueSnackbar('Urgent tag removed', { variant: 'success' });
      } else if (shipByDate && overrideShipByDate) {
        await dispatch(
          setLineItemShipByDate({
            lineItemId: lineItem.lineItemId,
            shipByDate: format(shipByDate, 'yyyy-MM-dd'),
            reason,
            mpi: lineItem.mpi,
            orderId: lineItem.orderId,
          })
        );
        enqueueSnackbar('Ship-by date override saved', { variant: 'success' });
      }

      onClose();
    } catch (error) {
      const message = error?.response?.errors?.[0]?.message || 'Failed to save changes';
      enqueueSnackbar(message, { variant: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (!lineItem) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby='ship-by-override-dialog-title'
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle id='ship-by-override-dialog-title'>
        <Typography variant='h6' fontWeight='bold'>
          Manage Ship By Date
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ overflowX: 'hidden' }}>
        {showPatientBanner && lineItem.patientName && (
          <PatientNameBanner patientName={lineItem.patientName} />
        )}
        {showPatientBanner && !lineItem.patientName && lineItem.mpi && (
          <PatientBanner mpi={lineItem.mpi} />
        )}

        <Box sx={{ mt: 1, mb: 2, ml: 1, width: '100%' }}>
          {[
            ...(lineItem.itemName ? [{ label: 'Drug Name', value: lineItem.itemName }] : []),
            ...(lineItem.strength ? [{ label: 'Strength', value: lineItem.strength }] : []),
            { label: 'Order ID', value: lineItem.orderId || 'N/A' },
            ...(lineItem.rxNumber ? [{ label: 'RX Number', value: lineItem.rxNumber }] : []),
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant='subtitle2' sx={{ minWidth: 110 }}>
                {label}
              </Typography>
              <Typography variant='subtitle2' sx={{ mx: 0.5 }}>
                :
              </Typography>
              <Typography variant='body2' fontWeight={600}>
                {value}
              </Typography>
            </Box>
          ))}

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant='subtitle2' sx={{ minWidth: 110 }}>
              Ship By Date
            </Typography>
            <Typography variant='subtitle2' sx={{ mx: 0.5 }}>
              :
            </Typography>
            <Typography variant='body2' fontWeight={600}>
              {formatShipByDate(lineItem.shipByDate)}
            </Typography>
            {isOverridden && !isCleared && (
              <Box sx={{ ml: 1 }}>
                <Chip
                  variant='outlined'
                  color='warning'
                  size='small'
                  label='Overridden'
                  onDelete={() => setIsCleared(true)}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Form
          key={formKey}
          onSubmit={handleSave}
          initialValues={formInitialValues}
          render={({ handleSubmit, submitting, values }) => {
            const isDateInPast = values.shipByDate && values.shipByDate < startOfDay(new Date());
            const hasValidDate = !!values.shipByDate && !isDateInPast;
            const getHasDateChanged = () => {
              if (!values.shipByDate) return false;
              if (!formInitialValues.shipByDate) return true;
              return (
                format(values.shipByDate, 'yyyy-MM-dd') !==
                format(formInitialValues.shipByDate, 'yyyy-MM-dd')
              );
            };
            const hasDateChanged = getHasDateChanged();
            const urgentAdding = urgentChecked && !isUrgent && hasValidDate;
            const urgentUpdating = urgentChecked && isUrgent && hasValidDate && hasDateChanged;
            const urgentRemoving = !urgentChecked && isUrgent && !isDateInPast;
            const dateOverriding =
              !urgentChecked && !isUrgent && hasValidDate && overrideShipByDate && hasDateChanged;
            const canSave =
              isCleared || urgentAdding || urgentUpdating || urgentRemoving || dateOverriding;

            return (
              <form noValidate onSubmit={handleSubmit}>
                <Divider sx={{ mb: 2 }} />

                {isCleared ? (
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    Override ship by date will be removed and ship by date will be restored to the
                    system-calculated ship by date on Save.
                  </Alert>
                ) : (
                  <Grid container direction='column' spacing={2}>
                    {/* Ship By Date picker — shown for users with overrideShipByDate permission */}
                    {overrideShipByDate && (
                      <>
                        <Grid item>
                          <DateField
                            name='shipByDate'
                            id='ship-by-override-date'
                            label='Ship By Date'
                            disablePast
                            validations={[]}
                          />
                          {isDateInPast && (
                            <Typography
                              variant='caption'
                              color='error'
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              Ship by date cannot be in the past
                            </Typography>
                          )}
                        </Grid>

                        <Grid item>
                          <Field
                            name='reason'
                            render={({ input: { name, value, onChange, onBlur, onFocus } }) => (
                              <TextField
                                name={name}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                onFocus={onFocus}
                                placeholder='Reason (optional)'
                                id='ship-by-override-reason'
                                multiline
                                rows={2}
                                fullWidth
                                variant='outlined'
                                inputProps={{ 'aria-label': 'Override reason' }}
                              />
                            )}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Urgent tag checkbox — shown for all users */}
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={urgentChecked}
                            onChange={(e) => setUrgentChecked(e.target.checked)}
                            size='small'
                          />
                        }
                        label={<Typography variant='body2'>Add Urgent Tag</Typography>}
                      />
                      {isUrgent && urgentChecked && (
                        <Typography variant='caption' color='text.secondary' display='block'>
                          Urgent tag is currently active. Uncheck to remove.
                        </Typography>
                      )}
                    </Grid>

                    {/* Date picker for urgent — shown only for users without overrideShipByDate when urgent is checked */}
                    {!overrideShipByDate && urgentChecked && (
                      <Grid item>
                        <DateField
                          name='shipByDate'
                          id='urgent-ship-by-date'
                          label='Ship By Date'
                          required
                          disablePast
                          validations={[]}
                        />
                        {isDateInPast && (
                          <Typography
                            variant='caption'
                            color='error'
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            Ship by date cannot be in the past
                          </Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant='outlined'
                    color='secondary'
                    onClick={() => onClose()}
                    type='button'
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type='submit'
                    variant='contained'
                    color='primary'
                    loading={submitting || saving}
                    disabled={submitting || saving || !canSave}
                  >
                    Save
                  </LoadingButton>
                </Box>
              </form>
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

ShipByDateOverrideDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showPatientBanner: PropTypes.bool,
  lineItem: PropTypes.shape({
    lineItemId: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired,
    itemName: PropTypes.string,
    strength: PropTypes.string,
    overrideReason: PropTypes.string,
    shipByDate: PropTypes.string,
    needsByDate: PropTypes.string,
    mpi: PropTypes.string,
    patientName: PropTypes.string,
    rxNumber: PropTypes.string,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        tagName: PropTypes.string.isRequired,
        isSystemTag: PropTypes.bool,
      })
    ),
  }),
};

ShipByDateOverrideDialog.defaultProps = {
  lineItem: null,
  showPatientBanner: false,
};

export default ShipByDateOverrideDialog;
